import { useNavigate } from 'react-router-dom';
import { Zap, ArrowLeft, Brain, Target, TrendingUp, BookOpen, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function WhyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      <nav className="glass-heavy sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#6366F1' }}>
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg tracking-tight" style={{ fontFamily: 'Syne' }}>Regret Engine</span>
          </div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm" style={{ color: '#94A3B8' }}>
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'rgba(99,102,241,0.15)' }}>
            <Brain className="w-4 h-4" style={{ color: '#6366F1' }} />
            <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#6366F1' }}>Why This Exists</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold mb-6" style={{ fontFamily: 'Syne', lineHeight: 1.2 }}>
            You Can't Google<br />
            <span style={{ color: '#6366F1' }}>Your Future</span>
          </h1>
          <p className="text-base max-w-xl mx-auto mb-8" style={{ color: '#94A3B8', lineHeight: 1.7 }}>
            Important choices are hard to hold in your head all at once. Regret Engine turns your own context into
            contrasting scenarios and questions so you can examine the trade-offs more deliberately.
          </p>
        </div>

        {/* The Problem */}
        <section className="mb-20">
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Syne' }}>The Problem</h2>
          <div className="glass-card rounded-xl p-8 space-y-4" style={{ color: '#CBD5E1', lineHeight: 1.8 }}>
            <p>
              A decision can feel certain while important assumptions remain invisible. Scenario rehearsal creates
              enough distance to notice those assumptions, but it does not reveal the future.
            </p>
            <p>
              Research on <a href="https://doi.org/10.1016/S0065-2601(03)01006-2" target="_blank" rel="noreferrer" className="underline" style={{ color: '#A5B4FC' }}>affective forecasting</a> examines
              how people anticipate future feelings, including recurring errors in judging their intensity or duration.
            </p>
            <p>
              Research also suggests that regrets about action and inaction can follow different patterns over time.
              The <a href="https://pubmed.ncbi.nlm.nih.gov/7740094/" target="_blank" rel="noreferrer" className="underline" style={{ color: '#A5B4FC' }}>Gilovich and Medvec review</a> discusses that pattern without implying that one option is universally safer.
            </p>
            <p>
              These studies motivate reflection; they do not supply a personal probability or validate Regret Engine's
              output. Your result is generated from your inputs and model synthesis, not a research database.
            </p>
          </div>
        </section>

        {/* The Solution */}
        <section className="mb-20">
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Syne' }}>The Approach: Scenario Rehearsal</h2>
          <p className="text-sm mb-8" style={{ color: '#94A3B8' }}>
            Regret Engine does not tell you what to do. It helps you imagine distinct paths, surface assumptions, and identify facts to verify.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              {
                icon: <BookOpen className="w-5 h-5" />,
                title: 'Describe Your Decision',
                desc: 'Tell the engine what you\'re deciding. Add context: salary, experience, what matters to you, what scares you.'
              },
              {
                icon: <Brain className="w-5 h-5" />,
                title: 'AI Drafts Possible Futures',
                desc: 'Three illustrative scenarios per option — optimistic, realistic, pessimistic — using your context and clearly labeled assumptions.'
              },
              {
                icon: <Target className="w-5 h-5" />,
                title: 'Anticipated Regret Reflection',
                desc: 'Each option gets a qualitative reflection label and explanation—not a numerical prediction dressed up as certainty.'
              },
              {
                icon: <TrendingUp className="w-5 h-5" />,
                title: 'Decision Lens',
                desc: 'Compare reversibility, downside, opportunity cost, values, uncertainty, and the facts that could change the analysis.'
              },
            ].map((item, i) => (
              <div key={i} className="glass-card rounded-xl p-6">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: 'rgba(99,102,241,0.15)', color: '#6366F1' }}>
                  {item.icon}
                </div>
                <h3 className="font-semibold mb-2" style={{ color: '#F8FAFC' }}>{item.title}</h3>
                <p className="text-sm" style={{ color: '#94A3B8', lineHeight: 1.6 }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Use Cases */}
        <section className="mb-20">
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Syne' }}>Who Uses It</h2>
          <div className="space-y-3">
            {[
              { title: 'Career Decisions', desc: 'Compare role changes, startup offers, further study, opportunity cost, and reversibility.' },
              { title: 'Education Decisions', desc: 'Examine tuition, time, foregone income, alternatives, and program outcomes you should verify.' },
              { title: 'Financial Decisions', desc: 'Surface liquidity, downside capacity, concentration, and missing figures—without inventing returns or market forecasts.' },
              { title: 'Relationship & Life', desc: 'Explore boundaries, values, communication, trade-offs, and safety without telling you what to choose.' },
            ].map((item, i) => (
              <div key={i} className="glass-card rounded-xl p-6 flex items-start gap-4">
                <div className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center text-sm font-bold" style={{ background: 'rgba(99,102,241,0.15)', color: '#6366F1' }}>
                  {i + 1}
                </div>
                <div>
                  <h3 className="font-semibold mb-1" style={{ color: '#F8FAFC' }}>{item.title}</h3>
                  <p className="text-sm" style={{ color: '#94A3B8', lineHeight: 1.6 }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* What Makes It Different */}
        <section className="mb-20">
          <div className="glass-card rounded-xl p-8 text-center" style={{ border: '1px solid rgba(99,102,241,0.3)' }}>
            <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Syne', color: '#F8FAFC' }}>
              Built for one focused job:<br />
              making a difficult choice easier to examine.
            </h2>
            <p className="text-base mb-6" style={{ color: '#6366F1', fontWeight: 600 }}>
              Side-by-side scenarios, explicit assumptions, and no pretend certainty.
            </p>
            <p className="text-sm max-w-lg mx-auto" style={{ color: '#94A3B8', lineHeight: 1.7 }}>
              The result uses your context to generate illustrative narratives and a qualitative decision lens.
              It is a mirror for reflection—not evidence, a prediction, or professional advice.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center mb-16">
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Syne' }}>Ready to Examine Your Decision?</h2>
          <p className="text-sm mb-8" style={{ color: '#94A3B8' }}>
            Try your first simulation free. No credit card required.
          </p>
          <Button
            onClick={() => navigate('/auth')}
            className="rounded-full px-8 py-4 text-sm font-medium"
            style={{ background: '#6366F1' }}
          >
            Start Your First Simulation <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </section>

        {/* Footer */}
        <div className="text-center pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <p className="text-xs" style={{ color: '#475569' }}>
            SolvAI Labs  |  Built by Ritik Barnwal  |  ritik@solvailabs.com
          </p>
        </div>
      </div>
    </div>
  );
}
